import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import LiveMatchControls from '../components/Match/LiveMatchControls';
import { 
  getMatch, 
  getTeam, 
  convertToSerializableMatch,
  Team
} from '../services/matchService';
import { RootState } from '../store/store';
import { setCurrentMatch } from '../store/matchSlice';

const LiveMatchPage: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [teamA, setTeamA] = useState<Team | null>(null);
  const [teamB, setTeamB] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentMatch = useSelector((state: RootState) => state.match.currentMatch);

  useEffect(() => {
    const initializeMatch = async () => {
      try {
        if (!matchId) {
          console.error('[MATCH INIT] No match ID found', {
            matchId,
            action: 'Redirecting to create match'
          });
          setError('Aucun ID de match trouvé');
          navigate('/create-match');
          return;
        }

        console.log('[MATCH INIT] Attempting to initialize match', {
          matchId,
          timestamp: new Date().toISOString()
        });

        // Vérifier si un match existe déjà
        const existingMatch = await getMatch(matchId);
        
        console.log('[MATCH INIT] Existing match check', {
          existingMatch: existingMatch ? 'Found' : 'Not found',
          matchStatus: existingMatch?.status
        });
        
        if (existingMatch) {
          // Charger les équipes
          const teamAData = existingMatch.teamA ? await getTeam(existingMatch.teamA.id || '') : null;
          const teamBData = existingMatch.teamB ? await getTeam(existingMatch.teamB.id || '') : null;

          console.log('[MATCH INIT] Team data', {
            teamA: teamAData ? teamAData.name : 'Not found',
            teamB: teamBData ? teamBData.name : 'Not found'
          });

          // Mettre à jour les états des équipes
          setTeamA(teamAData);
          setTeamB(teamBData);

          console.log('[MATCH INIT] Existing match check', { matchId, match: existingMatch });

          // Convertir le match en format sérialisable avant de le dispatcher à Redux
          console.log('[MATCH INIT] Avant conversion', { 
            matchId: existingMatch.id,
            dateType: existingMatch.date ? (existingMatch.date instanceof Timestamp ? 'Timestamp' : typeof existingMatch.date) : 'undefined',
            eventsCount: existingMatch.events?.length || 0
          });
          
          const serializableMatch = convertToSerializableMatch(existingMatch);
          
          console.log('[MATCH INIT] Après conversion', { 
            matchId: serializableMatch.id,
            dateType: typeof serializableMatch.date,
            eventsCount: serializableMatch.events?.length || 0
          });
          
          dispatch(setCurrentMatch(serializableMatch));

          setLoading(false);
        } else {
          // Gérer le cas où aucun match n'est trouvé
          console.error('[MATCH INIT] No match found');
          navigate('/create-match');
        }
      } catch (error) {
        console.error('[MATCH INIT] Error initializing match', error);
        setError('Impossible de charger le match');
        navigate('/create-match');
      }
    };

    initializeMatch();
  }, [matchId, navigate, dispatch]);

  // Rendu du composant
  if (loading) {
    return <div>Chargement...</div>;
  }

  if (error) {
    return <div>Erreur : {error}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {currentMatch && teamA && teamB && (
        <LiveMatchControls 
          match={currentMatch}
          teamA={teamA} 
          teamB={teamB} 
          matchId={matchId} 
        />
      )}
    </div>
  );
};

export default LiveMatchPage;
